/**
 * GameState — estado central do jogo Survival Hunter.
 * Balance v1: economia Bronze/Prata/Ouro, combate, defesa, sobrevivência 15 min.
 */

// ─── Moeda ───
// 1 prato = 10 Bronze, 100 Bronze = 1 Prata, 100 Prata = 1 Ouro
export const BRONZE_PER_PRATO = 10;
export const BRONZE_PER_PRATA = 100;
export const PRATA_PER_OURO = 100;

// ─── Cozinha ───
export const COOK_TIME = 3;           // segundos para assar 1 carne

// ─── Personagens — Stats Base ───
export const HERO_STATS   = { hp: 100, dano: 10, speed: 1.0, bag: 3 };
export const AJUDANTE_STATS = { hp: 60, dano: 5, speed: 0.9, bag: 4 };
export const GARCOM_STATS   = { hp: 50, dano: 2, speed: 1.0, bag: 3 };
export const CONTADOR_STATS = { hp: 45, dano: 1, speed: 0.8, bag: 50 }; // bag em bronze
export const ARQUEIRO_STATS = { hp: 55, dano: 8, bag: 10 }; // fixo em torre, bag = flechas

// ─── Monstros ───
export const MONSTER_TYPES = {
  selvagem:    { hp: 30, dano: 5,  focaCerca: false, ignoraCerca: false },
  agressivo:   { hp: 40, dano: 8,  focaCerca: false, ignoraCerca: false },
  destruidor:  { hp: 60, dano: 10, focaCerca: true,  ignoraCerca: false },
  voador:      { hp: 35, dano: 12, focaCerca: false, ignoraCerca: true },
} as const;

export type MonsterType = keyof typeof MONSTER_TYPES;

// Spawn rules — progressivo por tempo
// Ondas: 0-2min calmo, 2-5min medio, 5-10min forte, 10-15min caos
export const MONSTER_WAVES = [
  { startSec: 45,  intervalSec: 12, agressivoChance: 0,    voador: false, elite: false },  // onda 1: selvagens fracos (45s de graça)
  { startSec: 120, intervalSec: 8,  agressivoChance: 0.2,  voador: false, elite: false },  // onda 2: alguns agressivos
  { startSec: 300, intervalSec: 5,  agressivoChance: 0.4,  voador: true,  elite: false },  // onda 3: voadores aparecem
  { startSec: 420, intervalSec: 4,  agressivoChance: 0.5,  voador: true,  elite: true },   // onda 4: elites
  { startSec: 600, intervalSec: 3,  agressivoChance: 0.6,  voador: true,  elite: true },   // onda 5: caos
  { startSec: 720, intervalSec: 2,  agressivoChance: 0.7,  voador: true,  elite: true },   // onda 6: inferno
] as const;

export const MONSTER_SPAWN = {
  voadorEvery: 15,           // 1 voador a cada 15 agressivos (quando ativado)
  eliteEveryKills: 40,       // 1 elite a cada 40 mortos (quando ativado)
  eliteHp: 120,
  eliteDano: 20,
} as const;

// ─── Loot de Monstros (bronze) ───
export const MONSTER_LOOT: Record<string, number> = {
  selvagem: 5,
  agressivo: 10,
  destruidor: 15,
  voador: 12,
  elite: 25,
};

// Escalonamento: a cada 3 min
export const SCALING = {
  intervalSec: 180,          // 3 minutos
  hpMultiplier: 0.10,        // +10% vida
  danoMultiplier: 0.10,      // +10% dano
  spawnMultiplier: 0.05,     // +5% spawn rate
} as const;

// ─── Cerca ───
export const FENCE = {
  hpInicial: 500,
  reparoCustoBronze: 50,     // 50 bronze = +100 vida
  reparoHp: 100,
  upgrades: [
    { custoPrata: 2, bonusHp: 300 },
    { custoPrata: 5, bonusHp: 700 },
  ],
} as const;

// ─── Torres ───
export const TOWER = {
  custoPrata: 3,
  hp: 300,
  arqueiroCustoPrata: 2,
  dano: 8,
  fireRate: 1,               // 1 tiro/segundo
} as const;

// ─── Área de Descanso ───
export const REST_AREA = {
  capacidadeInicial: 3,
  consumoPratoPor10s: 1,     // 1 prato a cada 10s por pessoa
  curaComComida: 5,          // vida/segundo
  curaSemComida: 1,          // vida/segundo
  upgrades: [
    { custoPrata: 2, bonusCapacidade: 2 },
    { custoPrata: 5, bonusCapacidade: 5 },
  ],
} as const;

// ─── Escudo Orbital ───
export const ORBITAL_SHIELD = {
  custoPrata: 5,
  hp: 150,
  danoContato: 6,
  upgrades: [
    { custoPrata: 3, bonusHp: 50 },
    { custoPrata: 3, bonusDano: 3 },
  ],
} as const;

// ─── Contratação ───
export const HIRE_COST = {
  cacador: 100,              // bronze
  ajudante: 150,             // bronze
  garcom: 200,               // bronze
  contadorPrata: 1,          // prata
  arqueiroPrata: 2,          // prata
  inflacao: 0.20,            // +20% por contratação
} as const;

// ─── Vitória / Derrota ───
export const GAME_DURATION = 15 * 60; // 15 minutos em segundos

// ─── Animais (caça) ───
export const ANIMAL_HP = 3;
export const ANIMAL_RESPAWN = 10;
export const MAX_ANIMALS = 4;

// ─── Clientes ───
export const CLIENT_PATIENCE = 25;
export const MAX_CLIENTS_QUEUE = 5;
export const CARNE_SELL_VALUE = 10;  // bronze por prato vendido

// ─── Workers antigos (compatibilidade) ───
export const WORKER_SPEED = 100;
export const BACKPACK_CAPACITY = 3;
export const BAG_UPGRADE_COST = 50;  // bronze

export type InventoryType = 'none' | 'carne_crua' | 'carne_assada';
export type WorkerType = 'cacador' | 'ajudante' | 'garcom' | 'contador' | 'arqueiro';

// ─── Dados Salvos ───
export interface SaveData {
  // Moeda
  bronze: number;
  prata: number;
  ouro: number;
  // Cozinha
  depositoCarne: number;
  bancadaCarne: number;
  balcaoVenda: number;
  pratos: number;           // pratos prontos (para descanso)
  // Stats
  abates: number;
  monstrosMortos: number;
  clientesAtendidos: number;
  clientesPerdidos: number;
  satisfacaoTotal: number;
  // Workers
  workerCacador: number;
  workerAjudante: number;
  workerGarcom: number;
  workerContador: number;
  workerArqueiro: number;
  hireCount: number;        // total de contratações (para inflação)
  // Bags
  bagPlayer: number;
  bagAjudante: number;
  bagGarcom: number;
  bagContador: number;
  bagArqueiro: number;
  // Defesas
  cercaHp: number;
  cercaHpMax: number;
  cercaLevel: number;
  torreCount: number;
  // Descanso
  restCapacidade: number;
  restLevel: number;
  // Escudo
  hasShield: boolean;
  shieldHp: number;
  shieldHpMax: number;
  shieldDano: number;
  shieldLevel: number;
  // Fase / Tempo
  fase: number;
  gameTimeSec: number;      // tempo total de jogo (para vitória)
  scalingLevel: number;     // quantas vezes escalonou
}

export class GameState {
  // ─── Moeda ───
  bronze = 50;  // bronze inicial para primeiras compras
  prata = 0;
  ouro = 0;

  // ─── Cozinha ───
  depositoCarne = 0;
  bancadaCarne = 0;
  balcaoVenda = 0;
  pratos = 0;               // carne assada → prato servido

  // ─── Stats ───
  abates = 0;
  monstrosMortos = 0;
  clientesAtendidos = 0;
  clientesPerdidos = 0;
  satisfacaoTotal = 0;

  // ─── Workers ───
  workerCacador = 0;
  workerAjudante = 0;
  workerGarcom = 0;
  workerContador = 0;
  workerArqueiro = 0;
  hireCount = 0;

  // ─── Bags ───
  bagPlayer = HERO_STATS.bag;
  bagAjudante = AJUDANTE_STATS.bag;
  bagGarcom = GARCOM_STATS.bag;
  bagContador = CONTADOR_STATS.bag;
  bagArqueiro = ARQUEIRO_STATS.bag;

  // ─── Defesas ───
  cercaHp: number = FENCE.hpInicial;
  cercaHpMax: number = FENCE.hpInicial;
  cercaLevel = 0;
  torreCount = 0;

  // ─── Descanso ───
  restCapacidade: number = REST_AREA.capacidadeInicial;
  restLevel = 0;

  // ─── Escudo Orbital ───
  hasShield = false;
  shieldHp = 0;
  shieldHpMax = 0;
  shieldDano = 0;
  shieldLevel = 0;

  // ─── Fase / Tempo ───
  fase = 1;
  gameTimeSec = 0;
  scalingLevel = 0;

  // ─── Runtime (não salvo) ───
  fogueiraCarne = 0;
  fogueiraTimer = 0;
  playerInventoryType: InventoryType = 'none';
  playerInventoryCount = 0;
  playerHp: number = HERO_STATS.hp;
  playerHpMax: number = HERO_STATS.hp;

  // ─── Player Combat ───

  playerTakeDamage(amount: number) {
    this.playerHp = Math.max(0, this.playerHp - amount);
  }

  isPlayerDead(): boolean {
    return this.playerHp <= 0;
  }

  respawnPlayer() {
    this.playerHp = this.playerHpMax;
  }

  // ─── Moeda Helpers ───

  get money(): number { return this.bronze; }
  set money(v: number) { this.bronze = v; }

  canAffordBronze(amount: number): boolean { return this.bronze >= amount; }
  canAffordPrata(amount: number): boolean { return this.prata >= amount; }

  spendBronze(amount: number): boolean {
    if (this.bronze < amount) return false;
    this.bronze -= amount;
    return true;
  }

  spendPrata(amount: number): boolean {
    if (this.prata < amount) return false;
    this.prata -= amount;
    return true;
  }

  /** Converte bronze → prata automaticamente quando possível */
  autoConvertCurrency() {
    while (this.bronze >= BRONZE_PER_PRATA) {
      this.bronze -= BRONZE_PER_PRATA;
      this.prata++;
    }
    while (this.prata >= PRATA_PER_OURO) {
      this.prata -= PRATA_PER_OURO;
      this.ouro++;
    }
  }

  // ─── Player ───

  playerPickup(type: InventoryType, amount: number): boolean {
    if (type === 'none') return false;
    if (this.playerInventoryType !== 'none' && this.playerInventoryType !== type) return false;
    const space = this.bagPlayer - this.playerInventoryCount;
    if (space <= 0) return false;
    const toPickup = Math.min(amount, space);
    this.playerInventoryType = type;
    this.playerInventoryCount += toPickup;
    return true;
  }

  playerDropAll(): { type: InventoryType; count: number } {
    const result = { type: this.playerInventoryType, count: this.playerInventoryCount };
    this.playerInventoryType = 'none';
    this.playerInventoryCount = 0;
    return result;
  }

  isPlayerInventoryFull(): boolean {
    return this.playerInventoryCount >= this.bagPlayer;
  }

  // ─── Cozinha ───

  depositCarneNaMesa(amount: number) {
    this.depositoCarne += amount;
  }

  depositCarneNoBalcao(amount: number) {
    this.balcaoVenda += amount;
  }

  updateFogueira(dt: number) {
    if (this.fogueiraCarne === 0 && this.depositoCarne > 0) {
      this.depositoCarne--;
      this.fogueiraCarne = 1;
      this.fogueiraTimer = COOK_TIME;
    }
    if (this.fogueiraCarne > 0) {
      this.fogueiraTimer -= dt;
      if (this.fogueiraTimer <= 0) {
        this.fogueiraCarne = 0;
        this.fogueiraTimer = 0;
        this.bancadaCarne++;
      }
    }
  }

  sellCarne(): number {
    if (this.balcaoVenda <= 0) return 0;
    this.balcaoVenda--;
    this.bronze += CARNE_SELL_VALUE;
    return CARNE_SELL_VALUE;
  }

  // ─── Clientes ───

  registerClienteAtendido(satisfacao: number) {
    this.clientesAtendidos++;
    const clamped = Math.max(0, Math.min(10, satisfacao));
    this.satisfacaoTotal += clamped;
  }

  registerClientePerdido() {
    this.clientesPerdidos++;
  }

  getSatisfacaoMedia(): number {
    if (this.clientesAtendidos === 0) return -1;
    return this.satisfacaoTotal / this.clientesAtendidos;
  }

  // ─── Defesas ───

  repairCerca(): boolean {
    if (!this.canAffordBronze(FENCE.reparoCustoBronze)) return false;
    if (this.cercaHp >= this.cercaHpMax) return false;
    this.spendBronze(FENCE.reparoCustoBronze);
    this.cercaHp = Math.min(this.cercaHp + FENCE.reparoHp, this.cercaHpMax);
    return true;
  }

  upgradeCerca(): boolean {
    if (this.cercaLevel >= FENCE.upgrades.length) return false;
    const upg = FENCE.upgrades[this.cercaLevel];
    if (!this.canAffordPrata(upg.custoPrata)) return false;
    this.spendPrata(upg.custoPrata);
    this.cercaHpMax += upg.bonusHp;
    this.cercaHp += upg.bonusHp;
    this.cercaLevel++;
    return true;
  }

  buildTorre(): boolean {
    if (!this.canAffordPrata(TOWER.custoPrata)) return false;
    this.spendPrata(TOWER.custoPrata);
    this.torreCount++;
    return true;
  }

  // ─── Descanso ───

  upgradeRest(): boolean {
    if (this.restLevel >= REST_AREA.upgrades.length) return false;
    const upg = REST_AREA.upgrades[this.restLevel];
    if (!this.canAffordPrata(upg.custoPrata)) return false;
    this.spendPrata(upg.custoPrata);
    this.restCapacidade += upg.bonusCapacidade;
    this.restLevel++;
    return true;
  }

  // ─── Escudo Orbital ───

  buyShield(): boolean {
    if (this.hasShield) return false;
    if (!this.canAffordPrata(ORBITAL_SHIELD.custoPrata)) return false;
    this.spendPrata(ORBITAL_SHIELD.custoPrata);
    this.hasShield = true;
    this.shieldHp = ORBITAL_SHIELD.hp;
    this.shieldHpMax = ORBITAL_SHIELD.hp;
    this.shieldDano = ORBITAL_SHIELD.danoContato;
    return true;
  }

  upgradeShield(): boolean {
    if (!this.hasShield) return false;
    if (this.shieldLevel >= ORBITAL_SHIELD.upgrades.length) return false;
    const upg = ORBITAL_SHIELD.upgrades[this.shieldLevel];
    if (!this.canAffordPrata(upg.custoPrata)) return false;
    this.spendPrata(upg.custoPrata);
    if ('bonusHp' in upg) {
      this.shieldHpMax += upg.bonusHp;
      this.shieldHp += upg.bonusHp;
    }
    if ('bonusDano' in upg) {
      this.shieldDano += upg.bonusDano;
    }
    this.shieldLevel++;
    return true;
  }

  // ─── Escalonamento ───

  getScalingMultiplier(): number {
    return 1 + this.scalingLevel * SCALING.hpMultiplier;
  }

  getScaledMonsterHp(base: number): number {
    return Math.floor(base * this.getScalingMultiplier());
  }

  getScaledMonsterDano(base: number): number {
    return Math.floor(base * (1 + this.scalingLevel * SCALING.danoMultiplier));
  }

  updateScaling(dt: number) {
    this.gameTimeSec += dt;
    const expectedLevel = Math.floor(this.gameTimeSec / SCALING.intervalSec);
    if (expectedLevel > this.scalingLevel) {
      this.scalingLevel = expectedLevel;
    }
  }

  // ─── Vitória / Derrota ───

  isVictory(): boolean {
    return this.gameTimeSec >= GAME_DURATION;
  }

  isDefeat(): boolean {
    return this.cercaHp <= 0; // banco/descanso ficam vulneráveis quando cerca cai
  }

  // ─── Bag Upgrades ───

  upgradeBag(type: 'player' | 'ajudante' | 'garcom' | 'contador'): boolean {
    if (!this.canAffordBronze(BAG_UPGRADE_COST)) return false;
    this.spendBronze(BAG_UPGRADE_COST);
    switch (type) {
      case 'player': this.bagPlayer++; break;
      case 'ajudante': this.bagAjudante++; break;
      case 'garcom': this.bagGarcom++; break;
      case 'contador': this.bagContador++; break;
    }
    return true;
  }

  // ─── Workers ───

  getWorkerCount(type: WorkerType): number {
    switch (type) {
      case 'cacador': return this.workerCacador;
      case 'ajudante': return this.workerAjudante;
      case 'garcom': return this.workerGarcom;
      case 'contador': return this.workerContador;
      case 'arqueiro': return this.workerArqueiro;
    }
  }

  getMaxWorkers(type: WorkerType): number {
    const bonus = this.fase >= 6 ? 1 : 0;
    switch (type) {
      case 'cacador':  return (this.fase >= 3 ? 2 : 1) + bonus;
      case 'ajudante': return (this.fase >= 2 ? 2 : 1) + bonus;
      case 'garcom':   return (this.fase >= 2 ? 2 : 1) + bonus;
      case 'contador': return (this.fase >= 2 ? 1 : 0) + bonus;
      case 'arqueiro': return this.torreCount; // 1 arqueiro por torre
    }
  }

  getHireCostBronze(type: WorkerType): number {
    let base = 0;
    if (type === 'cacador') base = HIRE_COST.cacador;
    else if (type === 'ajudante') base = HIRE_COST.ajudante;
    else if (type === 'garcom') base = HIRE_COST.garcom;
    else return 0; // contador e arqueiro custam prata
    const inflacao = Math.pow(1 + HIRE_COST.inflacao, this.hireCount);
    return Math.floor(base * inflacao);
  }

  getHireCostPrata(type: WorkerType): number {
    if (type === 'contador') return HIRE_COST.contadorPrata;
    if (type === 'arqueiro') return HIRE_COST.arqueiroPrata;
    return 0;
  }

  canHireWorker(type: WorkerType): boolean {
    if (this.getWorkerCount(type) >= this.getMaxWorkers(type)) return false;
    const costBronze = this.getHireCostBronze(type);
    const costPrata = this.getHireCostPrata(type);
    if (costBronze > 0) return this.canAffordBronze(costBronze);
    if (costPrata > 0) return this.canAffordPrata(costPrata);
    return false;
  }

  hireWorker(type: WorkerType): boolean {
    if (!this.canHireWorker(type)) return false;
    const costBronze = this.getHireCostBronze(type);
    const costPrata = this.getHireCostPrata(type);
    if (costBronze > 0) this.spendBronze(costBronze);
    if (costPrata > 0) this.spendPrata(costPrata);
    switch (type) {
      case 'cacador': this.workerCacador++; break;
      case 'ajudante': this.workerAjudante++; break;
      case 'garcom': this.workerGarcom++; break;
      case 'contador': this.workerContador++; break;
      case 'arqueiro': this.workerArqueiro++; break;
    }
    this.hireCount++;
    return true;
  }

  // ─── Save/Load ───

  toSave(): SaveData {
    return {
      bronze: this.bronze,
      prata: this.prata,
      ouro: this.ouro,
      depositoCarne: this.depositoCarne,
      bancadaCarne: this.bancadaCarne,
      balcaoVenda: this.balcaoVenda,
      pratos: this.pratos,
      abates: this.abates,
      monstrosMortos: this.monstrosMortos,
      clientesAtendidos: this.clientesAtendidos,
      clientesPerdidos: this.clientesPerdidos,
      satisfacaoTotal: this.satisfacaoTotal,
      workerCacador: this.workerCacador,
      workerAjudante: this.workerAjudante,
      workerGarcom: this.workerGarcom,
      workerContador: this.workerContador,
      workerArqueiro: this.workerArqueiro,
      hireCount: this.hireCount,
      bagPlayer: this.bagPlayer,
      bagAjudante: this.bagAjudante,
      bagGarcom: this.bagGarcom,
      bagContador: this.bagContador,
      bagArqueiro: this.bagArqueiro,
      cercaHp: this.cercaHp,
      cercaHpMax: this.cercaHpMax,
      cercaLevel: this.cercaLevel,
      torreCount: this.torreCount,
      restCapacidade: this.restCapacidade,
      restLevel: this.restLevel,
      hasShield: this.hasShield,
      shieldHp: this.shieldHp,
      shieldHpMax: this.shieldHpMax,
      shieldDano: this.shieldDano,
      shieldLevel: this.shieldLevel,
      fase: this.fase,
      gameTimeSec: this.gameTimeSec,
      scalingLevel: this.scalingLevel,
    };
  }

  static fromSave(data: SaveData): GameState {
    const gs = new GameState();
    gs.bronze = data.bronze ?? 0;
    gs.prata = data.prata ?? 0;
    gs.ouro = data.ouro ?? 0;
    gs.depositoCarne = data.depositoCarne ?? 0;
    gs.bancadaCarne = data.bancadaCarne ?? 0;
    gs.balcaoVenda = data.balcaoVenda ?? 0;
    gs.pratos = data.pratos ?? 0;
    gs.abates = data.abates ?? 0;
    gs.monstrosMortos = data.monstrosMortos ?? 0;
    gs.clientesAtendidos = data.clientesAtendidos ?? 0;
    gs.clientesPerdidos = data.clientesPerdidos ?? 0;
    gs.satisfacaoTotal = data.satisfacaoTotal ?? 0;
    gs.workerCacador = data.workerCacador ?? 0;
    gs.workerAjudante = data.workerAjudante ?? 0;
    gs.workerGarcom = data.workerGarcom ?? 0;
    gs.workerContador = data.workerContador ?? 0;
    gs.workerArqueiro = data.workerArqueiro ?? 0;
    gs.hireCount = data.hireCount ?? 0;
    gs.bagPlayer = data.bagPlayer ?? HERO_STATS.bag;
    gs.bagAjudante = data.bagAjudante ?? AJUDANTE_STATS.bag;
    gs.bagGarcom = data.bagGarcom ?? GARCOM_STATS.bag;
    gs.bagContador = data.bagContador ?? CONTADOR_STATS.bag;
    gs.bagArqueiro = data.bagArqueiro ?? ARQUEIRO_STATS.bag;
    gs.cercaHp = data.cercaHp ?? FENCE.hpInicial;
    gs.cercaHpMax = data.cercaHpMax ?? FENCE.hpInicial;
    gs.cercaLevel = data.cercaLevel ?? 0;
    gs.torreCount = data.torreCount ?? 0;
    gs.restCapacidade = data.restCapacidade ?? REST_AREA.capacidadeInicial;
    gs.restLevel = data.restLevel ?? 0;
    gs.hasShield = data.hasShield ?? false;
    gs.shieldHp = data.shieldHp ?? 0;
    gs.shieldHpMax = data.shieldHpMax ?? 0;
    gs.shieldDano = data.shieldDano ?? 0;
    gs.shieldLevel = data.shieldLevel ?? 0;
    gs.fase = data.fase ?? 1;
    gs.gameTimeSec = data.gameTimeSec ?? 0;
    gs.scalingLevel = data.scalingLevel ?? 0;
    return gs;
  }

  static createNew(): GameState {
    return new GameState();
  }
}
